import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Fills the recently viewed section from the ids the theme stores locally.
 *
 * The ids are resolved server-side by refetching this section against the search
 * endpoint with Shopify's `id:` query syntax, so the cards come back fully rendered
 * rather than being rebuilt in JavaScript.
 *
 * @extends {Component<{}>}
 */
class RecentlyViewedProducts extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  async #render() {
    const grid = this.querySelector('[data-recently-viewed-grid]');
    if (!grid) return;

    // This same section is what gets fetched, so a populated grid means we are
    // looking at the fetched copy and there is nothing left to do.
    if (grid.children.length > 0) return;

    const excludeId = this.getAttribute('data-exclude-id');
    const limit = Number(this.getAttribute('data-limit')) || 4;
    const sectionId = this.getAttribute('data-section-id');
    if (!sectionId) return;

    const ids = RecentlyViewed.getProducts()
      .filter((id) => id !== excludeId)
      .slice(0, limit);

    if (ids.length === 0) return;

    const query = ids.map((id) => `id:${id}`).join(' OR ');
    const url = `${Theme.routes.search_url}?q=${encodeURIComponent(query)}&section_id=${encodeURIComponent(sectionId)}`;

    try {
      const response = await fetch(url, { signal: this.#abortController.signal });
      if (!response.ok) return;

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fetchedGrid = parsed.querySelector('[data-recently-viewed-grid]');
      if (!fetchedGrid || fetchedGrid.children.length === 0) return;

      grid.replaceChildren(...fetchedGrid.children);
      this.hidden = false;
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.warn('[recently-viewed] Could not load products:', error);
      }
    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
